import logger from '@platform/common/helpers/logger';

let registrationPromise: Promise<ServiceWorkerRegistration | null> | null = null;

/**
 * Регистрирует сервис-воркер приложения и возвращает регистрацию.
 * 
 * Выполняет ленивую инициализацию: первый вызов запускает регистрацию,
 * последующие — повторно используют уже созданный промис. Логирует обновления
 * и ошибки, чтобы было проще диагностировать проблемы с PWA и push.
 */
export function ensureServiceWorkerRegistration(): Promise<ServiceWorkerRegistration | null> {
  if (registrationPromise) {
    return registrationPromise;
  }

  if (!('serviceWorker' in navigator)) {
    logger.warn('⚠️ [PWA] Service Worker не поддерживается в этом браузере');
    registrationPromise = Promise.resolve(null);
    return registrationPromise;
  }

  registrationPromise = navigator.serviceWorker
    .register('/sw.js')
    .then((registration) => {
      logger.info('✅ [PWA] Service Worker зарегистрирован:', registration.scope);

      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing;
        if (newWorker) {
          logger.info('🔄 [PWA] Найдено обновление Service Worker');
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              logger.info('🆕 [PWA] Доступна обновлённая версия Service Worker');
            }
          });
        }
      });

      return registration;
    })
    .catch((error: unknown) => {
      logger.error('❌ [PWA] Не удалось зарегистрировать Service Worker:', error);
      return null;
    });

  return registrationPromise;
}

/**
 * Возвращает активную регистрацию сервис-воркера, ожидая её готовности.
 * 
 * Если регистрация недоступна, возвращает `null`, чтобы вызывающий код мог
 * деликатно обработать отсутствие PWA.
 */
export async function waitForServiceWorkerReady(): Promise<ServiceWorkerRegistration | null> {
  try {
    const registration = await ensureServiceWorkerRegistration();
    if (!registration) {
      return null;
    }

    const readyRegistration = await navigator.serviceWorker.ready;
    return readyRegistration;
  } catch (error) {
    logger.error('❌ [PWA] Ошибка при ожидании готовности Service Worker:', error);
    return null;
  }
}

